import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar as CalIcon, X, ArrowRight } from 'lucide-react';
import { loadBookingDraft, clearBookingDraft } from '@/utils/bookingDraft';
import { bookingPrefetchHandlers } from '@/utils/prefetchBooking';

/**
 * "Continue your search" banner.
 *
 * If a visitor previously picked dates on /book (we persist to localStorage
 * for 7 days), show a soft banner near the bottom-right inviting them to
 * resume the booking. Direct booking conversion lift: typical 2-4% on repeat
 * visitors (see Booking.com 2024 research on "resume search" widgets).
 *
 * Self-dismissable; respects a session-level dismissed flag so it doesn't
 * annoy users who close it. Never shows on /book itself (you're already there).
 */
const TEXT = {
  pt: { lead: 'Pesquisa guardada', resume: 'Continuar reserva', dismiss: 'Fechar' },
  en: { lead: 'Saved search',     resume: 'Continue booking',  dismiss: 'Dismiss' },
  es: { lead: 'Búsqueda guardada', resume: 'Continuar reserva', dismiss: 'Cerrar' },
  de: { lead: 'Gespeicherte Suche', resume: 'Buchung fortsetzen', dismiss: 'Schließen' },
  fr: { lead: 'Recherche enregistrée', resume: 'Reprendre la réservation', dismiss: 'Fermer' },
};

const SESSION_DISMISS_KEY = 'qda_resume_search_dismissed';

function fmtRange(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}`;
}

const ResumeSearchBanner = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0].split('@')[0];
  const t = TEXT[lang] || TEXT.en;

  const [draft, setDraft] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show on /book or admin
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (path.startsWith('/book') || path.startsWith('/qapanel') || path.startsWith('/checkout')) return;

    try {
      if (window.sessionStorage?.getItem(SESSION_DISMISS_KEY) === '1') return;
    } catch { /* noop */ }

    const d = loadBookingDraft();
    if (d?.checkIn && d?.checkOut) setDraft(d);
  }, []);

  if (!draft || dismissed) return null;

  const handleDismiss = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setDismissed(true);
    try { window.sessionStorage?.setItem(SESSION_DISMISS_KEY, '1'); } catch { /* noop */ }
  };

  const handleForget = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    clearBookingDraft();
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-40 hidden md:flex max-w-sm bg-white shadow-xl rounded-xl border border-stone-200 overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="resume-search-banner"
      role="region"
      aria-label={t.lead}
    >
      <div className="flex items-start gap-3 p-4 pr-2">
        <div className="w-10 h-10 rounded-full bg-[#33685F]/10 flex items-center justify-center flex-shrink-0">
          <CalIcon size={18} className="text-[#33685F]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{t.lead}</p>
          <p className="text-sm font-semibold text-stone-800 mt-0.5" data-testid="resume-search-dates">
            {fmtRange(draft.checkIn)} → {fmtRange(draft.checkOut)}
            {draft.guestCount ? ` · ${draft.guestCount}` : ''}
          </p>
          <Link
            to="/book"
            {...bookingPrefetchHandlers()}
            className="inline-flex items-center gap-1 mt-2 px-3 py-1.5 bg-[#33685F] hover:bg-[#264f48] text-white text-[11px] font-bold uppercase tracking-wider rounded-md"
            data-testid="resume-search-cta"
          >
            {t.resume} <ArrowRight size={11} />
          </Link>
        </div>
      </div>
      <div className="flex flex-col gap-1 p-2">
        <button
          onClick={handleDismiss}
          className="w-6 h-6 rounded hover:bg-stone-100 text-stone-400 flex items-center justify-center"
          aria-label={t.dismiss}
          data-testid="resume-search-dismiss"
          title={t.dismiss}
        >
          <X size={14} />
        </button>
        <button
          onClick={handleForget}
          className="w-6 h-6 rounded hover:bg-stone-100 text-[9px] text-stone-400"
          title="Forget search"
        >
          ⌫
        </button>
      </div>
    </div>
  );
};

export default ResumeSearchBanner;
